import { GlobalStateController } from "bai-react-global-state";
import * as flexsearch from "flexsearch";
import _ from "lodash";

import { workspaceResources as workspaceResourcesApi } from "apis-v2/callables";
import { PusherEventType } from "common/constants";
import { fetchCompressedIndexData, getEncoderOptions, search as performSearch } from "common/flexsearch";
import { IWorkspaceDocumentChangedEvent, IWorkspacePermission, IWorkspaceResource, IWorkspaceResourceWithDocument, PaginatedFilter, PaginatedResponse, PaginatedSort, WorkspacePermissionObjectType, WorkspaceResourceType } from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import { getSequentialRunner, SequentialRunner } from "common/utils/runSequentially";
import { serverContext } from "js/config";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import pusher, { ExtendedChannel } from "js/core/services/pusher";

const logger = getLogger();

export interface BaseWorkspaceResourcesControllerState<TDocument, TResource> {
    filters: PaginatedFilter[];
    sort: PaginatedSort[];
    total: number;
    data: IWorkspaceResourceWithDocument<TDocument, TResource>[];
    fetching: boolean;
    initialized: boolean;
    tags: {
        tag: string;
        count: number;
    }[];
    search: {
        query: string;
        fields?: string[];
    }
    resourceTypes: WorkspaceResourceType[];
    workspaceId: string;
}

const initialState: BaseWorkspaceResourcesControllerState<unknown, IWorkspaceResource> = {
    filters: [],
    sort: [],
    total: 0,
    data: [],
    search: {
        query: ""
    },
    fetching: false,
    initialized: false,
    tags: [],
    resourceTypes: [],
    workspaceId: null,
};

export class BaseWorkspaceResourcesController<TDocument, TResource extends IWorkspaceResource, TSearchDocument> extends GlobalStateController<BaseWorkspaceResourcesControllerState<TDocument, TResource>> {
    protected _runSequentially: SequentialRunner;
    protected _index: flexsearch.Document<any>;
    protected _indexPromise: Promise<void>;
    protected _pusherChannel: ExtendedChannel;
    protected _unsubscribeWorkspaceControllerStateDidUpdate: () => void;

    constructor() {
        super(_.cloneDeep(initialState as BaseWorkspaceResourcesControllerState<TDocument, TResource>));

        this._runSequentially = getSequentialRunner();
    }

    public get initialized() {
        return this._state.initialized;
    }

    protected get _baseFilters(): PaginatedFilter[] {
        return [];
    }

    protected get _batchSize() {
        return 20;
    }

    protected get _documentFields(): string[] {
        throw new Error("Not implemented");
    }

    protected _searchDocumentToResourceWithDocument(searchDocument: TSearchDocument): IWorkspaceResourceWithDocument<TDocument, TResource> {
        throw new Error("Not implemented");
    }

    protected async _fetchSearchIndex({ locale, workspaceId }: { locale: string, workspaceId: string }): Promise<{ indexConfig: flexsearch.DocumentOptions<any>, indexFileReadUrl: string }> {
        throw new Error("Not implemented");
    }

    protected async _prepareIndex() {
        const { workspaceId } = this._state;

        // Wait for a tick to let the caller set this._indexPromise value so we can capture it
        await new Promise(resolve => setTimeout(resolve, 0));
        const currentIndexPromise = this._indexPromise;

        const locale = serverContext.locale ?? "en";
        const { indexConfig, indexFileReadUrl } = await this._fetchSearchIndex({ locale, workspaceId });

        const data = await fetchCompressedIndexData(indexFileReadUrl);

        const index = new flexsearch.Document({
            encoder: getEncoderOptions(locale),
            ...indexConfig,
        });
        data.forEach(({ key, data }) => index.import(key, data));

        if (currentIndexPromise !== this._indexPromise) {
            // New request came in
            return;
        }

        this._index = index;
    }

    protected async _ensureIndexPrepared() {
        if (!this._indexPromise) {
            this._indexPromise = this._prepareIndex();
        }
        return this._indexPromise;
    }

    protected _disposeIndex() {
        this._indexPromise = null;
        this._index = null;
    }

    protected async _fetchResourcesFromIndex({
        offset,
        size
    }: {
        offset: number;
        size: number;
    }) {
        const { filters, search } = this._state;

        await this._ensureIndexPrepared();

        const results = performSearch({
            index: this._index,
            query: search.query,
            fields: search.fields as flexsearch.FieldName<TSearchDocument>[]
        });

        let items: IWorkspaceResourceWithDocument<TDocument, TResource>[] = [];
        results.forEach(result => {
            const resourceWithDocument = this._searchDocumentToResourceWithDocument(result);
            if (!resourceWithDocument) {
                return;
            }

            if (items.some(item => item.resource.id === resourceWithDocument.resource.id)) {
                return;
            }

            items.push(resourceWithDocument);
        });

        // Client-side filtering and pagination //

        // !!!! MUST stay in sync with common-backend/mongo/aggregations/Pagination.ts !!!! //

        for (const filter of filters) {
            items = items.filter(item => {
                const value = _.get(item, filter.field);
                const filterValueArray = Array.isArray(filter.value) ? filter.value : [filter.value];

                switch (filter.operator) {
                    case "eq":
                        if (Array.isArray(value)) {
                            return value.includes(filter.value);
                        }
                        return value === filter.value;
                    case "ne":
                        if (Array.isArray(value)) {
                            return !value.includes(filter.value);
                        }
                        return value !== filter.value;
                    case "gt":
                        return value > filter.value;
                    case "gte":
                        return value >= filter.value;
                    case "lt":
                        return value < filter.value;
                    case "lte":
                        return value <= filter.value;
                    case "in":
                        return filterValueArray.includes(value);
                    case "nin":
                        return !filterValueArray.includes(value);
                }
                return true;
            });
        }

        // Important: sorting is forced to be score based for full text search queries

        const total = items.length;

        items = items.slice(offset, offset + size);

        return {
            total,
            items
        } as PaginatedResponse<IWorkspaceResourceWithDocument<TDocument, TResource>>;
    }

    protected async _fetchResources({
        offset,
        size
    }: {
        offset: number;
        size: number;
    }) {
        const { filters, sort, resourceTypes, workspaceId, search } = this._state;

        if (search.query) {
            // Full text search query is present, fetch results from the index
            // and do client-side filtering and sorting
            return await this._fetchResourcesFromIndex({ offset, size });
        }

        return await workspaceResourcesApi.getWorkspaceResourcesWithDocuments({
            workspaceId,
            resourceTypes,
            offset,
            size,
            sort,
            filters,
            documentFields: this._documentFields
        }) as PaginatedResponse<IWorkspaceResourceWithDocument<TDocument, TResource>>;
    }

    protected async _loadMoreData() {
        const { data } = this._state;

        this._updateState({ fetching: true });

        try {
            const { total, items } = await this._fetchResources({
                offset: data.length,
                size: this._batchSize
            });

            const ids = items.map(({ resource }) => resource.id);

            await this._updateState(prevState => ({
                ...prevState,
                total,
                data: [..._.cloneDeep(prevState.data.filter(({ resource }) => !ids.includes(resource.id))), ...items]
            }));
        } catch (err) {
            logger.error(err, "Failed to load more resources");
            throw err;
        } finally {
            await this._updateState({ fetching: false });
        }
    }

    protected async _reloadData(clearCurrentState = false) {
        if (clearCurrentState) {
            await this._updateState({ data: [], total: 0, fetching: true });
        } else {
            await this._updateState({ fetching: true });
        }

        try {
            const { total, items } = await this._fetchResources({
                offset: 0,
                size: this._batchSize
            });

            await this._updateState(prevState => ({
                ...prevState,
                total,
                data: items
            }));
        } catch (err) {
            logger.error(err, "Failed to reload resources");
            throw err;
        } finally {
            await this._updateState({ fetching: false });
        }
    }

    protected getFiltersForTags(filters: PaginatedFilter[]) {
        return filters
            .filter(f => f.field !== "resource.tags")
            .filter(f => f.field.startsWith("resource."))
            .map(f => ({
                ...f,
                field: f.field.replace("resource.", "")
            }));
    }

    protected async _fetchTags() {
        const { resourceTypes, workspaceId, filters } = this._state;

        return await workspaceResourcesApi.getWorkspaceResourcesTags({
            workspaceId,
            resourceTypes,
            filters: this.getFiltersForTags(filters)
        });
    }

    protected async _reloadTags(clearCurrentState = false) {
        if (clearCurrentState) {
            await this._updateState({ tags: [] });
        }

        const tags = await this._fetchTags();

        await this._updateState({ tags });
    }

    protected _stateDidUpdate(prevState: BaseWorkspaceResourcesControllerState<TDocument, TResource>) {
        const { filters, sort, resourceTypes, initialized, search } = this._state;

        if (!initialized) {
            return;
        }

        this._runSequentially(async () => {
            if (getObjectHash(prevState.resourceTypes) !== getObjectHash(resourceTypes)) {
                this._disposeIndex();

                await Promise.all([
                    this._reloadTags(true),
                    this._reloadData(true)
                ]);
                return;
            }

            if (
                getObjectHash(prevState.filters) !== getObjectHash(filters) ||
                getObjectHash(prevState.sort) !== getObjectHash(sort) ||
                (prevState.search.query !== search.query || (search.query && getObjectHash(prevState.search.fields) !== getObjectHash(search.fields)))
            ) {
                const keepState = search.query && prevState.search.query;
                await this._reloadData(!keepState);

                if (getObjectHash(this.getFiltersForTags(prevState.filters)) !== getObjectHash(this.getFiltersForTags(filters))) {
                    await this._reloadTags();
                }
                return;
            }
        });
    }

    protected async _handleExternalResourcesChange({
        added,
        removed,
        updated
    }: {
        added: IWorkspaceResource[];
        removed: IWorkspaceResource[];
        updated: IWorkspaceResource[];
    }) {
        const { data, resourceTypes } = this._state;

        added = added.filter(({ resourceType }) => resourceTypes.includes(resourceType));
        removed = removed.filter(({ resourceType }) => resourceTypes.includes(resourceType));
        updated = updated.filter(({ resourceType }) => resourceTypes.includes(resourceType));

        if (added.length > 0) {
            this._disposeIndex();

            // Reload the data as we cannot know if the new resources skew the current filtered results, so we're just
            // reloading the whole thing
            await Promise.all([
                this._reloadTags(),
                this._reloadData()
            ]);
            return;
        }

        let disposeIndex = false;
        for (const resource of [...updated, ...removed]) {
            const record = data.find(record => record.resource.id === resource.id);
            if (record && (record.resource.modifiedAt < resource.modifiedAt || removed.includes(resource))) {
                // Force refreshing the resource might make so the resource doesn't match the filter anymore,
                // we're fine with it as long as the resource itself is in correct state
                await this._forceRefreshResource({ resourceId: resource.resourceId, resourceType: resource.resourceType });
                disposeIndex = true;
            }

            // If the record is not in state, then we don't know if it matches the current filters and if it should be
            // in the current set based on sorting and its position, so we're just ignoring it for simplicity
        }

        if (disposeIndex) {
            this._disposeIndex();
        }
    }

    protected _onWorkspaceControllerStateDidUpdate = (prevState: WorkspaceControllerState) => {
        this._runSequentially(async () => {
            const resources = WorkspaceController.resources;
            const resourcesBefore = prevState.resources;

            await this._handleExternalResourcesChange({
                added: resources.filter(resource => !resourcesBefore.some(r => r.id === resource.id)),
                removed: resourcesBefore.filter(resource => !resources.some(r => r.id === resource.id)),
                updated: resources.filter(resource => resourcesBefore.some(r => r.id === resource.id && r.modifiedAt !== resource.modifiedAt))
            });
        });
    }

    protected _onPusherEvent = (event: IWorkspaceDocumentChangedEvent) => {
        this._runSequentially(async () => {
            const { data } = this._state;

            if (event.documentType === "WorkspaceResource") {
                const workspaceResource = event.operationType === "delete" ? event.oldDocument : event.newDocument;

                await this._handleExternalResourcesChange({
                    added: event.operationType === "create" ? [workspaceResource] : [],
                    removed: event.operationType === "delete" ? [workspaceResource] : [],
                    updated: event.operationType === "update" ? [workspaceResource] : []
                });

                return;
            }

            if (event.documentType === "WorkspacePermission") {
                if (event.operationType !== "delete") {
                    return;
                }

                const workspacePermission = event.oldDocument as IWorkspacePermission;
                if (workspacePermission.objectType !== WorkspacePermissionObjectType.WORKSPACE_RESOURCE) {
                    return;
                }

                const id = workspacePermission.objectId;
                const record = data.find(({ resource }) => resource.id === id);
                if (!record) {
                    return;
                }

                this._disposeIndex();

                await this._forceRefreshResource({ resourceId: record.resource.resourceId, resourceType: record.resource.resourceType });
            }
        });
    }

    protected async _forceRefreshResource({ resourceId, resourceType }: { resourceId: string, resourceType: WorkspaceResourceType }) {
        const { workspaceId, resourceTypes, data } = this._state;

        const recordBefore = data.find(({ resource }) => resource.resourceId === resourceId);

        const { items: [record] } = await workspaceResourcesApi.getWorkspaceResourcesWithDocuments({
            workspaceId,
            resourceTypes,
            offset: 0,
            size: 1,
            documentFields: this._documentFields,
            resources: [{ resourceId, resourceType }]
        });

        let tags: BaseWorkspaceResourcesControllerState<TDocument, TResource>["tags"] = null;
        if (!record || (record && recordBefore && getObjectHash(recordBefore.resource.tags) !== getObjectHash(record.resource.tags))) {
            tags = await this._fetchTags();
        }

        await this._updateState(state => {
            let data = record
                ? _.cloneDeep(state.data.map(item => item.resource.resourceId === resourceId ? record : item))
                : _.cloneDeep(state.data.filter(({ resource }) => resource.resourceId !== resourceId));

            if (record && state.sort.length > 0 && !state.search.query) {
                // Sorting may have changed after the resource was refreshed
                for (const sort of [...state.sort].reverse()) {
                    data = data.sort((a, b) => {
                        const valueA = _.get(a, sort.field);
                        const valueB = _.get(b, sort.field);
                        if (valueA === valueB) {
                            return 0;
                        }
                        return sort.direction === "asc" ? (valueA > valueB ? 1 : -1) : (valueA < valueB ? 1 : -1);
                    });
                }
            }

            return {
                ...state,
                tags: tags ?? state.tags,
                data
            };
        });
    }

    public get canLoadMore() {
        const { data, total } = this._state;

        if (data.length === 0) {
            return false;
        }

        return data.length < total;
    }

    public async setFilters(filters: BaseWorkspaceResourcesControllerState<TDocument, TResource>["filters"]) {
        return this._runSequentially(() => this._updateState({ filters }));
    }

    public async setSort(sort: BaseWorkspaceResourcesControllerState<TDocument, TResource>["sort"]) {
        return this._runSequentially(() => this._updateState({ sort }));
    }

    public async setSearch(search: BaseWorkspaceResourcesControllerState<TDocument, TResource>["search"]) {
        return this._runSequentially(() => this._updateState({ search }));
    }

    public async loadMore() {
        return this._runSequentially(() => this._loadMoreData());
    }

    public async reload() {
        return this._runSequentially(() => this._reloadData(true));
    }

    public async reset() {
        this._indexPromise = null;
        this._index = null;

        if (this._unsubscribeWorkspaceControllerStateDidUpdate) {
            this._unsubscribeWorkspaceControllerStateDidUpdate();
            this._unsubscribeWorkspaceControllerStateDidUpdate = null;
        }

        if (this._pusherChannel) {
            this._pusherChannel.unbind(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);
            if (!this._pusherChannel.isInUse) {
                pusher.unsubscribe(this._pusherChannel.name);
            }
            this._pusherChannel = null;
        }

        await this._runSequentially(() => this._updateState(_.cloneDeep(initialState) as BaseWorkspaceResourcesControllerState<TDocument, TResource>));
    }

    public async initialize({ filters, sort, search, resourceTypes, workspaceId }: { filters?: PaginatedFilter[], sort?: PaginatedSort[], search?: BaseWorkspaceResourcesControllerState<TDocument, TResource>["search"], resourceTypes: WorkspaceResourceType[], workspaceId: string }) {
        await this.reset();

        await this._runSequentially(async () => {
            this._pusherChannel = await pusher.subscribe(`private-workspace-${workspaceId}`);
            this._pusherChannel.bind(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);

            // Must be decoupled in the future //
            this._unsubscribeWorkspaceControllerStateDidUpdate = WorkspaceController.onStateDidUpdate(this._onWorkspaceControllerStateDidUpdate);

            await this._updateState({
                filters: filters ?? [],
                sort: sort ?? [],
                search: search ?? { query: "" },
                resourceTypes,
                workspaceId,
            });

            await Promise.all([
                this._reloadTags(),
                this._reloadData()
            ]);

            await this._updateState({ initialized: true });
        });
    }
}
